import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  TextField,
  Autocomplete,
  Pagination,
  CircularProgress,
  Alert,
  Paper,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { CiImageOff } from 'react-icons/ci';
import { useSearchParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import useUserStore from '../stores/userStore';
import useCartGuard from '../hooks/useCartGuard';

const OtcProducts = () => {
  // Theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  
  // User store
  const { addOtcToCart, fetchCart, fetchCartCount } = useUserStore();
  const { requireAuth } = useCartGuard();
  
  // State management
  const [products, setProducts] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    manufacturers: [],
    sub_categories: [],
    price_range: { min: 0, max: 1000 }
  });
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [],
    manufacturers: [],
    sub_categories: [],
    price_range: { min: 0, max: 1000 },
    search: '',
    ordering: 'name' // Default sorting by name
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cartSyncInProgress, setCartSyncInProgress] = useState(false);
  const isClearingFiltersRef = useRef(false);

  // Helpers to avoid redundant state updates
  const arraysEqual = (a = [], b = [], mapFn = (x) => x) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const aa = a.map(mapFn);
    const bb = b.map(mapFn);
    for (let i = 0; i < aa.length; i++) {
      if (aa[i] !== bb[i]) return false;
    }
    return true;
  };

  // Build a stable hash for filters to avoid useEffect loops due to object identity
  const filtersHash = useMemo(() => {
    const categoryNames = (selectedFilters.categories || []).map(cat => (typeof cat === 'object' ? cat.name : cat)).join('|');
    const manufacturerNames = (selectedFilters.manufacturers || []).map(m => (typeof m === 'object' ? m.name : m)).join('|');
    const subCategoryNames = (selectedFilters.sub_categories || []).map(sc => (typeof sc === 'object' ? String(sc) : sc)).join('|');
    const min = selectedFilters.price_range?.min ?? 0;
    const max = selectedFilters.price_range?.max ?? 1000;
    return [page, selectedFilters.search || '', selectedFilters.ordering || 'name', min, max, categoryNames, manufacturerNames, subCategoryNames].join('::');
  }, [page, selectedFilters.categories, selectedFilters.manufacturers, selectedFilters.sub_categories, selectedFilters.search, selectedFilters.ordering, selectedFilters.price_range]);

  // Load products when the filtersHash changes (stable primitive dependency)
  useEffect(() => {
    loadProducts();
  }, [filtersHash]);

  // Load filter options on component mount
  useEffect(() => {
    loadFilterOptions();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle URL category parameter when filter options are loaded
  useEffect(() => {
    // Prevent re-applying filters when we're clearing them
    if (isClearingFiltersRef.current) return;
    
    const categoryParam = searchParams.get('category');
    if (!categoryParam || filterOptions.categories.length === 0) return;

    // Find the matching category
    const matchingCategory = filterOptions.categories.find(category => 
      category.name === categoryParam || category.name === decodeURIComponent(categoryParam)
    );
    if (!matchingCategory) return;

    // Guard: do not set if already selected
    const alreadySelected = (selectedFilters.categories || []).some(cat => (cat?.id === matchingCategory.id) || (cat?.name === matchingCategory.name));
    if (alreadySelected) return;

    console.log('Setting category from URL parameter:', matchingCategory);
    setSelectedFilters(prev => ({
      ...prev,
      categories: [matchingCategory]
    }));
    setPage(1);
    // Scroll to top when category filter is applied from URL
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filterOptions.categories, searchParams, selectedFilters.categories]);

  // Update manufacturers and sub-categories when categories change (cascading filters)
  useEffect(() => {
    if (selectedFilters.categories.length > 0) {
      updateCascadingFilters();
    } else {
      // Reset manufacturers and sub-categories when no categories selected
      // Only update state if there is something to clear to avoid loops
      setFilterOptions(prev => {
        const needUpdate = (prev.manufacturers?.length || 0) > 0 || (prev.sub_categories?.length || 0) > 0;
        if (!needUpdate) return prev;
        return {
          ...prev,
          manufacturers: [],
          sub_categories: []
        };
      });
      setSelectedFilters(prev => {
        const needUpdate = (prev.manufacturers?.length || 0) > 0 || (prev.sub_categories?.length || 0) > 0;
        if (!needUpdate) return prev;
        return {
          ...prev,
          manufacturers: [],
          sub_categories: []
        };
      });
    }
  }, [selectedFilters.categories]);

  // Sync cart data when component mounts
  useEffect(() => {
    const initializeCartSync = async () => {
      try {
        await syncCartWithProducts();
      } catch (error) {
        console.error('Error initializing cart sync:', error);
      }
    };
    
    initializeCartSync();
  }, []);

  // Sync cart data with products
  const syncCartWithProducts = async () => {
    if (cartSyncInProgress) {
      console.log('Cart sync already in progress, skipping...');
      return;
    }
    
    try {
      setCartSyncInProgress(true);
      console.log('Starting cart sync...');
      
      const cartData = await fetchCart();
      if (cartData && cartData.cart && cartData.cart.cart_items && cartData.cart.cart_items.length > 0) {
        const cartItems = cartData.cart.cart_items;
        
        // Create a map of product IDs to cart quantities
        const cartMap = cartItems.reduce((acc, cartItem) => {
          if (cartItem.otc) {
            acc[cartItem.otc.id] = cartItem.quantity;
          }
          return acc;
        }, {});
        
        // Update products with cart quantities ONLY if quantity_in_cart was undefined
        let anyChanged = false;
        const updatedProducts = products.map(product => {
          const existing = product.quantity_in_cart;
          if (existing === undefined) {
            anyChanged = true;
            return { ...product, quantity_in_cart: cartMap[product.id] ?? null };
          }
          return product;
        });

        if (anyChanged) {
          setProducts(updatedProducts);
          console.log('Cart sync updated products with cart quantities');
        } else {
          console.log('Cart sync skipped setProducts (no changes)');
        }
        console.log('Cart sync completed successfully');
      }
    } catch (error) {
      console.error('Error syncing cart with products:', error);
    } finally {
      setCartSyncInProgress(false);
    }
  };

  // Initialize cart property for products when component mounts
  useEffect(() => {
    if (products.length > 0) {
      // Only sync if products don't have cart property initialized
      const needsCartSync = products.some(product => product.quantity_in_cart === undefined);
      if (needsCartSync) {
        syncCartWithProducts();
      }
    }
  }, [products]);

  // Load products from API
  const lastParamsHashRef = useRef('');
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page,
        category: selectedFilters.categories, // Pass category objects directly
        manufacturer: selectedFilters.manufacturers,
        sub_categories: selectedFilters.sub_categories,
        name: selectedFilters.search || undefined,
        min_price: selectedFilters.price_range.min > 0 ? selectedFilters.price_range.min : undefined,
        max_price: selectedFilters.price_range.max < 1000 ? selectedFilters.price_range.max : undefined,
        ordering: selectedFilters.ordering
      };
      // Create a stable hash of params to prevent duplicate API calls
      const paramsHash = JSON.stringify({
        page: params.page,
        name: params.name || '',
        ordering: params.ordering || 'name',
        min_price: params.min_price ?? 0,
        max_price: params.max_price ?? 1000,
        categories: (params.category || []).map(cat => (typeof cat === 'object' ? cat.name : cat)),
        manufacturers: (params.manufacturer || []).map(m => (typeof m === 'object' ? m.name : m)),
        sub_categories: (params.sub_categories || []).map(sc => (typeof sc === 'object' ? String(sc) : sc)),
      });
      if (lastParamsHashRef.current === paramsHash) {
        console.log('Skipping duplicate loadProducts call (same params)');
        setLoading(false);
        return;
      }
      lastParamsHashRef.current = paramsHash;

      console.log('Loading products with params:', params);
      
      // Try POST method first (advanced filtering)
      let result;
      try {
        result = await userService.getOtcProducts(params);
        console.log('Products API response (POST):', result);
      } catch (postError) {
        console.log('POST method failed, trying GET method:', postError);
        // Fallback to GET method
        result = await userService.getOtcProductsGET(params);
        console.log('Products API response (GET):', result);
      }
      
      if (result.data && result.data.results) {
        setProducts(result.data.results);
        setTotalPages(result.data.num_pages || 1);
        setTotalCount(result.data.count || 0);
        
        console.log(`Loaded ${result.data.results.length} products, ${result.data.num_pages} pages`);
        
        // Cart sync will be handled by the useEffect when products change
      } else {
        console.log('No products found or invalid response structure');
        setProducts([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error loading products:', err);
      setProducts([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Load filter options from the comprehensive API
  const loadFilterOptions = async () => {
    try {
      setFiltersLoading(true);
      const result = await userService.getFilterOptions();
      
      console.log('Filter options result:', result);
      
      if (result.data) {
        const options = result.data;
        setFilterOptions({
          categories: options.categories || [],
          manufacturers: options.manufacturers || [],
          sub_categories: options.sub_categories || [],
          price_range: options.price_range || { min: 0, max: 1000 }
        });
        
        console.log(`Loaded ${options.categories?.length || 0} categories, ${options.manufacturers?.length || 0} manufacturers, ${options.sub_categories?.length || 0} sub-categories`);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
      // Fallback to individual API calls
      await loadFiltersFallback();
    } finally {
      setFiltersLoading(false);
    }
  };

  // Fallback method to load filters individually
  const loadFiltersFallback = async () => {
    try {
      const [categoriesResult, brandsResult] = await Promise.all([
        userService.getCategories(),
        userService.getManufacturer()
      ]);
      
      console.log('Fallback - Categories result:', categoriesResult);
      console.log('Fallback - Brands result:', brandsResult);
      
      // Categories API now returns array of objects with id and name properties
      const categoriesResponse = categoriesResult.data || categoriesResult || [];
      const categories = categoriesResponse.map(category => ({
        id: category.id,
        name: category.name
      }));
      
      // Manufacturers API returns paginated response with objects containing name property
      const manufacturersResponse = brandsResult.data || brandsResult || {};
      const manufacturers = manufacturersResponse.results ? 
        manufacturersResponse.results.map(manufacturer => manufacturer.name).filter(name => name != null && name !== '') : 
        [];
      
      console.log(`Fallback loaded ${categories.length} categories and ${manufacturers.length} manufacturers`);
      
      setFilterOptions(prev => ({
        ...prev,
        categories: categories,
        manufacturers: manufacturers
      }));
    } catch (err) {
      console.error('Error in fallback filter loading:', err);
      setFilterOptions(prev => ({
        ...prev,
        categories: [],
        manufacturers: []
      }));
    }
  };

  // Update cascading filters when categories change
  const updateCascadingFilters = async () => {
    try {
      const categoryNames = selectedFilters.categories.map(cat => cat.name || cat);
      
      const [manufacturersResult, subCategoriesResult] = await Promise.all([
        userService.getManufacturersByCategories(categoryNames),
        userService.getSubCategoriesByCategories(categoryNames)
      ]);
      
      console.log('Cascading filters - Manufacturers:', manufacturersResult);
      console.log('Cascading filters - Sub-categories:', subCategoriesResult);
      
      const manufacturers = manufacturersResult.data || manufacturersResult || [];
      const subCategories = subCategoriesResult.data || subCategoriesResult || [];
      
      // Filter out null/undefined values and ensure proper data structure
      const cleanManufacturers = manufacturers.filter(item => item != null && item !== '');
      const cleanSubCategories = subCategories.filter(item => item != null && item !== '');
      
      setFilterOptions(prev => ({
        ...prev,
        manufacturers: cleanManufacturers,
        sub_categories: cleanSubCategories
      }));
      
      // Reset selected manufacturers and sub-categories since options changed
      setSelectedFilters(prev => ({
        ...prev,
        manufacturers: [],
        sub_categories: []
      }));
      
    } catch (err) {
      console.error('Error updating cascading filters:', err);
      // Set empty arrays on error to prevent null values
      setFilterOptions(prev => ({
        ...prev,
        manufacturers: [],
        sub_categories: []
      }));
    }
  };


  // Add to cart
  const addToCart = useCallback(async (productId) => {
    if (!requireAuth()) return;
    try {
      setLoadingStates(prev => ({ ...prev, [productId]: true }));
      await addOtcToCart(productId);
      
      // Update only the specific product in the local state without triggering full re-render
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === productId 
            ? { ...product, quantity_in_cart: (product.quantity_in_cart || 0) + 1 }
            : product
        )
      );
      
      setSnackbar({ open: true, message: 'Item added to cart successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error adding to cart:', err);
      setSnackbar({ open: true, message: 'Failed to add item to cart', severity: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  }, [addOtcToCart, requireAuth]);

  // Remove from cart
  const removeFromCart = useCallback(async (productId) => {
    if (!requireAuth()) return;
    try {
      setLoadingStates(prev => ({ ...prev, [productId]: true }));
      
      const result = await userService.removeOCart(productId);
      
      if (result.status === 200) {
        // Update only the specific product in the local state
        setProducts(prevProducts => 
          prevProducts.map(product => 
            product.id === productId 
              ? { ...product, quantity_in_cart: Math.max(0, (product.quantity_in_cart || 0) - 1) }
              : product
          )
        );
        
        // Update cart count
        await fetchCartCount();
        
        setSnackbar({ open: true, message: 'Item removed from cart successfully!', severity: 'success' });
      }
    } catch (err) {
      console.error('Error removing from cart:', err);
      setSnackbar({ open: true, message: 'Failed to remove item from cart', severity: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  }, [fetchCartCount, requireAuth]);

  // Get product image
  const getProductImage = (images) => {
    if (!images) {
      return null;
    }
    
    // Handle both array and string formats
    let firstImage;
    if (Array.isArray(images)) {
      // If images is an array, get the first URL
      firstImage = images[0];
    } else {
      // If images is a string, split and get the first image name
      const imageNames = images.split(',');
      firstImage = imageNames[0].trim();
      // Construct the path to the images_1mg_otc folder in public assets
      firstImage = `/assets/images_1mg_otc/${firstImage}`;
    }
    
    return firstImage || null;
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Handle category filter change
  const handleCategoryChange = (event, newValue) => {
    console.log('Category change - newValue:', newValue);
    setSelectedFilters(prev => {
      const same = arraysEqual(
        prev.categories,
        newValue,
        (cat) => (typeof cat === 'object' ? cat.id ?? cat.name : cat)
      );
      if (same) return prev;
      return {
        ...prev,
        categories: newValue
      };
    });
    setPage(1); // Reset to first page when filters change
  };

  // Handle manufacturer filter change
  const handleManufacturerChange = (event, newValue) => {
    setSelectedFilters(prev => {
      const same = arraysEqual(
        prev.manufacturers,
        newValue,
        (m) => (typeof m === 'object' ? m.name : m)
      );
      if (same) return prev;
      return {
        ...prev,
        manufacturers: newValue
      };
    });
    setPage(1); // Reset to first page when filters change
  };

  // Handle sub-category filter change
  const handleSubCategoryChange = (event, newValue) => {
    setSelectedFilters(prev => {
      const same = arraysEqual(
        prev.sub_categories,
        newValue,
        (sc) => (typeof sc === 'object' ? String(sc) : sc)
      );
      if (same) return prev;
      return {
        ...prev,
        sub_categories: newValue
      };
    });
    setPage(1); // Reset to first page when filters change
  };

  // Handle price range change
  const handlePriceRangeChange = (field, value) => {
    setSelectedFilters(prev => {
      const current = prev.price_range || { min: 0, max: 1000 };
      if ((field === 'min' && current.min === value) || (field === 'max' && current.max === value)) {
        return prev;
      }
      return {
        ...prev,
        price_range: {
          ...current,
          [field]: value
        }
      };
    });
    setPage(1); // Reset to first page when filters change
  };


  // Handle search change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSelectedFilters(prev => {
      if ((prev.search || '') === value) return prev;
      return { ...prev, search: value };
    });
    setPage(1); // Reset to first page when filters change
  };

  // Handle sorting change
  const handleSortingChange = (event, newValue) => {
    const orderingValue = typeof newValue === 'string' ? newValue : newValue?.value;
    setSelectedFilters(prev => {
      const current = prev.ordering || 'name';
      if (current === orderingValue) return prev;
      return { ...prev, ordering: orderingValue };
    });
    setPage(1); // Reset to first page when sorting changes
  };

  // Clear all filters
  const clearAllFilters = () => {
    // Set flag to prevent URL param useEffect from re-applying filters
    isClearingFiltersRef.current = true;
    
    // Fully reset filters to defaults and page to 1
    setSelectedFilters(prev => {
      // Avoid unnecessary object identity churn by checking if already default
      const isDefault = 
        (prev.categories?.length || 0) === 0 &&
        (prev.manufacturers?.length || 0) === 0 &&
        (prev.sub_categories?.length || 0) === 0 &&
        (prev.search || '') === '' &&
        (prev.ordering || 'name') === 'name' &&
        ((prev.price_range?.min ?? 0) === 0) &&
        ((prev.price_range?.max ?? 1000) === 1000);
      if (isDefault) return prev;
      return {
        categories: [],
        manufacturers: [],
        sub_categories: [],
        price_range: { min: 0, max: 1000 },
        search: '',
        ordering: 'name'
      };
    });
    setPage(1);
    // Remove any URL params (like category) that can re-apply filters
    setSearchParams({});
    
    // Reset flag after state updates are processed
    setTimeout(() => {
      isClearingFiltersRef.current = false;
    }, 0);
  };


  // Handle product card click
  const handleProductClick = (productId) => {
    navigate(`/otc/${productId}`);
  };



  // Render product card
  const renderProductCard = (product) => (
    <Grid 
      item 
      xs={12}
      sm={6}
      md={4}
      lg={3}
      key={product.id}
    >
      <Card 
        onClick={() => handleProductClick(product.id)}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
         width:'270px',
          height: '100%',
          boxSizing: 'border-box',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            borderColor: 'rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        {/* Product Image Section */}
        <Box sx={{ 
          position: 'relative',
          width: '100%',
          height: { xs: '160px', sm: '170px', md: '140px' },
          flexShrink: 0,
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          {/* Prescription Badge - Top Right */}
          {/* {product.prescription_required && (
            <Chip 
              label="Rx"
              size="small"
              sx={{ 
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 2,
                backgroundColor: '#ff9800',
                color: '#000000',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                borderRadius: '4px'
              }} 
            />
          )} */}
          
          {/* Discount Badge - Top Left */}
          {product.best_price && product.price && parseFloat(product.best_price) < parseFloat(product.price) && product.discount_percent && (
            <Chip 
              label={`${Math.round(parseFloat(product.discount_percent) * 100)}% OFF`}
              size="small"
              sx={{ 
                position: 'absolute',
                top: 6,
                left: 6,
                zIndex: 2,
                backgroundColor: '#4caf50',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                borderRadius: '4px'
              }}
            />
          )}

          {/* Image Container with Padding */}
          <Box sx={{
            width: '100%',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // padding: { xs: '0px', sm: '0px', md: '0px' },
            boxSizing: 'border-box'
          }}>
            {/* Product Image */}
            {getProductImage(product.images) ? (
              <CardMedia
                component="img"
                image={getProductImage(product.images)}
                
                className="product-image"
                sx={{ 
                  objectFit: 'cover',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto'
                }}
              />
            ) : (
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9e9e9e'
              }}>
                <CiImageOff size={40} />
                <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.65rem', color: '#9e9e9e' }}>
                  No Image
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Product Info Section */}
        <CardContent sx={{ 
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          padding: { xs: '8px', sm: '10px', md: '12px' },
          minHeight: 0,
          boxSizing: 'border-box',
          '&:last-child': { pb: { xs: '8px', sm: '10px', md: '12px' } }
        }}>
          {/* Category Badge */}
          {product.category && (
            <Chip
              label={product.category}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                mb: 0.5,
                height: '18px',
                fontSize: '0.625rem',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 500,
                flexShrink: 0,
                border: 'none',
                borderRadius: '4px'
              }}
            />
          )}

          {/* Product Name - Fixed 2 lines with ellipsis */}
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              mb: 0.25,
              letterSpacing: '-0.01em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              height: '2.6em',
              flexShrink: 0
            }}
          >
            {product.name}
          </Typography>

          {/* Sub-category */}
          {product.sub_category && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.65rem',
                color: 'text.secondary',
                fontWeight: 700,
                mb: 0.5,
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                // opacity: 0.9
              }}
            >
              {product.sub_category}
            </Typography>
          )}

          {/* Spacer to push bottom content down */}
          <Box sx={{ flex: '1 1 auto', minHeight: 0 }} />

          {/* Packaging and Manufacturer - Sticks to bottom of content area */}
          <Box sx={{ flexShrink: 0, pt: 0.5 }}>
            {product.pack_info || product.packaging ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  mb: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  // opacity: 0.65,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  lineHeight: 1.4
                }}
              >
                {product.pack_info || product.packaging}
              </Typography>
            ) : null}
            
            {product.manufacturer && (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.7rem',
                  color: 'primary.dark',
                  fontWeight: 900,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.65,
                  lineHeight: 1.4,
                  textTransform: 'uppercase',
                }}
              >
                {product.manufacturer}
              </Typography>
            )}
          </Box>
        </CardContent>

        {/* Price and Action Section */}
        <Box sx={{ 
          padding: { xs: '8px', sm: '10px', md: '12px' },
          paddingTop: '8px',
          borderTop: '1px solid',
          borderColor: '#e0e0e0',
          backgroundColor: '#fafafa',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          gap: 0.75
        }}>
          {/* Price Section */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1.15rem', sm: '1.25rem', md: '1.35rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}
                >
                  ₹{parseFloat(product.best_price || product.price).toFixed(2)}
                </Typography>
                
                {product.best_price && product.price && parseFloat(product.best_price) < parseFloat(product.price) && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      textDecoration: 'line-through',
                      opacity: 0.6,
                      lineHeight: 1.2
                    }}
                  >
                    ₹{parseFloat(product.price).toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Action Button */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: '36px', sm: '38px', md: '40px' }
          }}>
            {product.stock_status === 'out_of_stock' ? (
              <Button
                fullWidth
                variant="outlined"
                disabled
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  py: 0.75,
                  px: 2,
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e',
                  height: '100%',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5'
                }}
              >
                Out of Stock
              </Button>
            ) : !product.quantity_in_cart || product.quantity_in_cart <= 0 ? (
              <Button
                fullWidth
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  addToCart(product.id);
                }}
                disabled={loadingStates[product.id]}
                sx={{ 
                  backgroundColor: '#1976d2',
                  color: '#ffffff',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  py: 0.75,
                  px: 2,
                  borderRadius: '4px',
                  boxShadow: 'none',
                  height: '100%',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e'
                  }
                }}
              >
                {loadingStates[product.id] ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Add to Cart'
                )}
              </Button>
            ) : (
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.75,
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                padding: { xs: '4px 8px', sm: '5px 10px' },
                width: '100%',
                minHeight: { xs: '36px', sm: '38px', md: '40px' }
              }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromCart(product.id);
                  }}
                  disabled={!product.quantity_in_cart || product.quantity_in_cart <= 0 || loadingStates[product.id]}
                  sx={{ 
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    color: '#1976d2',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                    },
                    '&:disabled': {
                      backgroundColor: '#ffffff',
                      opacity: 0.4
                    }
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </Button>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    color: '#1976d2',
                    minWidth: '32px',
                    textAlign: 'center'
                  }}
                >
                  {product.quantity_in_cart || 0}
                </Typography>
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    addToCart(product.id);
                  }}
                  disabled={loadingStates[product.id]}
                  sx={{ 
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    color: '#1976d2',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  {loadingStates[product.id] ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddIcon fontSize="small" />
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    </Grid>
  );

  // Don't render until filter options are loaded to prevent object rendering errors
  if (filtersLoading && filterOptions.categories.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
            Loading filter options...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
          Pharma Man Beauty & Wellness Products
        </Typography>
      </Box> */}

      {/* Filters Section - Top Row */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Search Row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label="Search Products"
              placeholder="Search by product name..."
              value={selectedFilters.search}
              onChange={handleSearchChange}
              sx={{ maxWidth: { xs: '100%', sm: '300px' } }}
            />
            <Button 
              variant="outlined" 
              size="small" 
              onClick={clearAllFilters}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Clear All
            </Button>
          </Box>

          {/* Filter Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Categories */}
            <Autocomplete
              multiple
              options={filterOptions.categories || []}
              value={selectedFilters.categories || []}
              onChange={handleCategoryChange}
              disabled={filtersLoading}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: '200px' }, maxWidth: { xs: '100%', sm: '250px' } }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.name || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                return option.id === value.id;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categories"
                  placeholder="Select categories"
                  disabled={filtersLoading}
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={typeof option === 'string' ? option : (option?.name || '')}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            {/* Manufacturers */}
            <Autocomplete
              multiple
              options={filterOptions.manufacturers?.filter(option => option != null) || []}
              value={selectedFilters.manufacturers || []}
              onChange={handleManufacturerChange}
              disabled={filtersLoading || selectedFilters.categories.length === 0}
              filterSelectedOptions
              freeSolo
              size="small"
              sx={{ minWidth: { xs: '100%', sm: '180px' }, maxWidth: { xs: '100%', sm: '320px' } }}
              getOptionLabel={(option) => {
                if (option == null) return '';
                if (typeof option === 'string') return option;
                return option?.name || String(option);
              }}
              isOptionEqualToValue={(option, value) => {
                if (option == null || value == null) return false;
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                return option === value;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manufacturers"
                  placeholder={selectedFilters.categories.length === 0 ? "Select categories first" : "Search manufacturers"}
                  disabled={filtersLoading || selectedFilters.categories.length === 0}
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={typeof option === 'string' ? option : (option?.name || String(option))}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            {/* Sub-categories */}
            <Autocomplete
              multiple
              options={filterOptions.sub_categories?.filter(option => option != null) || []}
              value={selectedFilters.sub_categories || []}
              onChange={handleSubCategoryChange}
              disabled={filtersLoading || selectedFilters.categories.length === 0}
              filterSelectedOptions
              freeSolo
              size="small"
              sx={{ minWidth: { xs: '100%', sm: '180px' }, maxWidth: { xs: '100%', sm: '220px' } }}
              getOptionLabel={(option) => {
                if (option == null) return '';
                return typeof option === 'string' ? option : String(option);
              }}
              isOptionEqualToValue={(option, value) => {
                if (option == null || value == null) return false;
                return option === value;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sub-categories"
                  placeholder={selectedFilters.categories.length === 0 ? "Select categories first" : "Search sub-categories"}
                  disabled={filtersLoading || selectedFilters.categories.length === 0}
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={typeof option === 'string' ? option : String(option)}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            {/* Sorting */}
            <Autocomplete
              options={[
                { value: 'name', label: 'Name (A-Z)' },
                { value: '-name', label: 'Name (Z-A)' },
                { value: 'price', label: 'Price (Low to High)' },
                { value: '-price', label: 'Price (High to Low)' },
                { value: 'best_price', label: 'Best Price (Low to High)' },
                { value: '-best_price', label: 'Best Price (High to Low)' },
                { value: 'discount_percent', label: 'Discount % (Low to High)' },
                { value: '-discount_percent', label: 'Discount % (High to Low)' },
                { value: 'category__name', label: 'Category (A-Z)' },
                { value: '-category__name', label: 'Category (Z-A)' },
                { value: 'manufacturer__name', label: 'Manufacturer (A-Z)' },
                { value: '-manufacturer__name', label: 'Manufacturer (Z-A)' }
              ]}
              value={[
                { value: 'name', label: 'Name (A-Z)' },
                { value: '-name', label: 'Name (Z-A)' },
                { value: 'price', label: 'Price (Low to High)' },
                { value: '-price', label: 'Price (High to Low)' },
                { value: 'best_price', label: 'Best Price (Low to High)' },
                { value: '-best_price', label: 'Best Price (High to Low)' },
                { value: 'discount_percent', label: 'Discount % (Low to High)' },
                { value: '-discount_percent', label: 'Discount % (High to Low)' },
                { value: 'category__name', label: 'Category (A-Z)' },
                { value: '-category__name', label: 'Category (Z-A)' },
                { value: 'manufacturer__name', label: 'Manufacturer (A-Z)' },
                { value: '-manufacturer__name', label: 'Manufacturer (Z-A)' }
              ].find(option => option.value === selectedFilters.ordering) || { value: 'name', label: 'Name (A-Z)' }}
              onChange={handleSortingChange}
              disabled={filtersLoading}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: '180px' }, maxWidth: { xs: '100%', sm: '220px' } }}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sort By"
                  placeholder="Select sorting option"
                  disabled={filtersLoading}
                  size="small"
                />
              )}
            />
          </Box>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box>
        {/* Result Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total products: {totalCount} • Page {page} of {totalPages}
          </Typography>
        </Box>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Products Grid */}
        {!loading && (
          <Grid 
            container 
            spacing={{ xs: 2, sm: 2.5, md: 3 }}
            sx={{ 
              width: '100%',
              margin: 0,
              display: 'flex',
              flexWrap: 'wrap'
            }}
          >
            {products.map(renderProductCard)}
          </Grid>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {/* No Products Message */}
        {!loading && products.length === 0 && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No products found matching your criteria.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OtcProducts;
